import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { RootState } from "../../../../store";
import { getAllAddresses } from "../../../../Redux/customer-address/slice";
import { getCart, selectAddress } from "../../../../Redux/custmoer_cart_service/slice";
import {
    CartType,
    CouponType,
    ProductType,
} from "../../../../Redux/custmoer_cart_service/types";
import {
    clearCheckoutError,
    clearCheckoutState,
    processCheckout,
} from "../../../../Redux/Custmoer_Checkout/slice";
import {
    CheckoutDeliveryAddressDto,
    CheckoutDeliveryOptionDto,
    CheckoutDto,
    SalarPayType,
} from "../../../../Redux/Custmoer_Checkout/types";
import { getWalletBalance } from "../../../../Redux/wallet_apis/slice";
import { startCashfreeCheckout } from "../../../../utility/cashfreeCheckout";

import CheckoutNavbar from "../CheckoutNavbar";
import CheckoutAddressSection from "./CheckoutAddressSection/CheckoutAddressSection";
import CheckoutPaymentOptions from "./CheckoutPaymentOptions/CheckoutPaymentOptions";
import CheckoutPriceDetails from "./CheckoutPriceDetails/CheckoutPriceDetails";
import CheckoutReviewOrder from "./CheckoutReviewOrder/CheckoutReviewOrder";
import { CheckoutPricing } from "../CheckoutPricingTypes";
import "./CheckoutPage.css";

const COMPONENT_TO_SALAR_PAY: Record<string, string> = {
    REFERRAL_COMMISSION: "REFERRAL_COMMISSION",
    FLOW_COMMISSION: "SALAR_FLOW_COMMISSION",
    SAMRIDDHI_COMMISSION: "SALAR_SAMRIDDHI_COMMISSION",
    COMMUNITY_GROWTH_COMMISSION: "SALAR_COMMUNITY_GROWTH_COMMISSION",
    UNITY_COMMISSION: "SALAR_UNITY_COMMISSION",
    PEARLS_COMMISSION: "SALAR_PEARLS_COMMISSION",
    MILESTONE_REFERRAL_COMMISSION: "SALAR_MILESTONE_REFERRAL_COMMISSION",
    PARTNER_GROWTH_REFERRAL: "SALAR_PARTNER_GROWTH_REFERRAL",
    FUNDS: "SALAR_FUNDS",
    SHOPPING_VOUCHER: "SHOPPING_VOUCHER",
};

const CheckoutPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { cart } = useAppSelector((state: RootState) => state.customerCart);
    const { addresses } = useAppSelector(
        (state: RootState) => state.customerAddress
    );
    const { pdpDelivery, pdpDeliveryLoading } = useAppSelector(
        (state: RootState) => state.shipping
    );
    const {
        loading: checkoutLoading,
        checkoutResult,
        success: checkoutSuccess,
    } = useAppSelector((state: RootState) => state.checkout);
    const { balance } = useAppSelector((state: RootState) => state.wallet);
    const { user } = useAppSelector((state: RootState) => state.customerAuth);

    const [selectedWallets, setSelectedWallets] = useState<string[]>([]);
    const [selectedOnlineMethod, setSelectedOnlineMethod] = useState<string>("CASHFREE");
    const [deliveryInstructions, setDeliveryInstructions] = useState<string>("");
    const [paymentSuccessOpen, setPaymentSuccessOpen] = useState(false);
    const [isPlacingOrderState, setIsPlacingOrderState] = useState(false);

    // Prevent double-submission regardless of Redux loading state
    const isPlacingOrder = useRef(false);

    useEffect(() => {
        if (addresses.length === 0) dispatch(getAllAddresses());
        dispatch(getCart());

        return () => {
            dispatch(clearCheckoutState()); // reset on unmount
        };
    }, [dispatch, addresses.length]);

    // Auto-select default address (or first available) if cart has no address selected
    useEffect(() => {
        if (addresses && addresses.length > 0 && cart) {
            const isSelectedValid = addresses.some(
                (addr) => addr._id === cart.selectedAddressId
            );
            if (!cart.selectedAddressId || !isSelectedValid) {
                const defaultAddr =
                    addresses.find((addr) => addr.defaultAddress) || addresses[0];
                if (defaultAddr?._id) {
                    dispatch(
                        selectAddress({
                            data: { addressId: defaultAddr._id },
                            cartType: (cart.cartType as CartType) || CartType.ECOMMERCE,
                        })
                    );
                }
            }
        }
    }, [addresses, cart, dispatch]);

    useEffect(() => {
        if (user?._id) {
            dispatch(getWalletBalance());
        }
    }, [dispatch, user?._id]);

    // Handle payment launch on checkout success
    useEffect(() => {
        if (checkoutSuccess && checkoutResult) {
            const result = checkoutResult;
            dispatch(clearCheckoutState());

            const launchPayment = async () => {
                const sessionId =
                    result.paymentSessionId ||
                    (result as any)?.payment_session_id ||
                    (result as any)?.session_id;

                // Gateway step if sessionId present
                if (sessionId) {
                    try {
                        await startCashfreeCheckout(sessionId, result.paymentMode);
                    } catch (err: any) {
                        toast.error(err?.message || "Failed to open Cashfree checkout", {
                            autoClose: 4000,
                        });
                        navigate("/MyOrders");
                    }
                    return;
                }

                // Fully covered via wallet / funds
                toast.success("Order placed successfully!", {
                    autoClose: 3000,
                });
                setPaymentSuccessOpen(true);
            };

            void launchPayment();
        }
    }, [checkoutSuccess, checkoutResult, navigate, dispatch]);

    const hasPhysicalItem =
        cart?.items.some((item) => item.productType === ProductType.PHYSICAL) ??
        false;

    const selectedAddress = addresses.find(
        (addr) => addr._id === cart?.selectedAddressId
    );

    const pinMatches =
        !!pdpDelivery &&
        !!selectedAddress?.pincode &&
        pdpDelivery.customerPincode === selectedAddress.pincode;

    const deliveryAvailable =
        pinMatches &&
        (pdpDelivery.delhivery?.serviceable === true ||
            pdpDelivery.hyperlocal?.serviceable === true ||
            pdpDelivery.deliveryOptions?.some((o) => o.available) === true);

    const undeliverable =
        hasPhysicalItem &&
        !!selectedAddress?.pincode &&
        pinMatches &&
        !pdpDeliveryLoading &&
        !deliveryAvailable;

    const payDisabled =
        checkoutLoading ||
        !cart ||
        (hasPhysicalItem && pdpDeliveryLoading) ||
        undeliverable ||
        (hasPhysicalItem && !cart.deliveryOptionId);

    const handleSelectAddress = (addressId: string) => {
        dispatch(
            selectAddress({
                data: { addressId },
                cartType: (cart?.cartType as CartType) || CartType.ECOMMERCE,
            })
        );
    };

    const handleToggleWallet = (type: string) => {
        setSelectedWallets((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    const handlePlaceOrder = async () => {
        if (isPlacingOrder.current || checkoutSuccess) return;
        isPlacingOrder.current = true;
        setIsPlacingOrderState(true);

        const abort = (msg: string) => {
            toast.warn(msg, { autoClose: 3000 });
            isPlacingOrder.current = false;
            setIsPlacingOrderState(false);
        };

        if (!cart) {
            abort("No active cart found");
            return;
        }

        if (hasPhysicalItem && !cart.selectedAddressId) {
            abort("Please select a delivery address");
            return;
        }

        if (hasPhysicalItem && !cart.deliveryOptionId) {
            abort("Please select a delivery option");
            return;
        }

        if (hasPhysicalItem && pdpDeliveryLoading) {
            abort("Checking delivery options — please wait a moment");
            return;
        }

        if (undeliverable) {
            abort(
                `This order cannot be delivered to ${selectedAddress?.pincode}. Please choose a different address.`
            );
            return;
        }

        if (
            hasPhysicalItem &&
            (cart.deliveryOptionId === "sameday" || cart.deliveryOptionId === "fast") &&
            cart.metadata?.deliveryCourierCompanyId == null
        ) {
            abort("Same-day delivery is unavailable. Please choose Standard Delivery.");
            return;
        }

        const selectedAddressData = addresses.find(
            (addr) => addr._id === cart.selectedAddressId
        );
        if (!selectedAddressData) {
            abort("Selected address not found");
            return;
        }

        const deliveryAddress: CheckoutDeliveryAddressDto = {
            fullName: selectedAddressData.fullName,
            email: selectedAddressData.email || "",
            mobile: selectedAddressData.phoneNumber || "",
            addressLine1: selectedAddressData.addressLine1,
            addressLine2: selectedAddressData.addressLine2,
            landmark: selectedAddressData.landmark,
            city: selectedAddressData.city,
            district: selectedAddressData.district,
            state: selectedAddressData.state,
            country: selectedAddressData.country,
            pincode: selectedAddressData.pincode,
            latitude: selectedAddressData.latitude,
            longitude: selectedAddressData.longitude,
        };

        const isSameDay =
            cart.deliveryOptionId === "sameday" || cart.deliveryOptionId === "fast";
        const isExpress = cart.deliveryOptionId === "exp";
        const selectedDeliveryPrice = Number(cart.deliveryOptionPrice ?? 0);
        const selectedCourierCompanyId =
            cart.metadata?.deliveryCourierCompanyId != null
                ? Number(cart.metadata.deliveryCourierCompanyId)
                : undefined;

        const fromCart = Number(cart.deliveryEstimatedDays);
        const fromPdp = Number(pdpDelivery?.delhivery?.estimatedDays);
        const estimatedDays = Math.max(
            1,
            isSameDay
                ? 1
                : isExpress
                    ? Number.isFinite(fromCart) && fromCart > 0
                        ? fromCart
                        : 5
                    : Number.isFinite(fromPdp) && fromPdp >= 0
                        ? fromPdp
                        : Number.isFinite(fromCart) && fromCart >= 0
                            ? fromCart
                            : 2
        );

        const deliveryOption: CheckoutDeliveryOptionDto = {
            type: isSameDay ? "SAME_DAY" : isExpress ? "EXPRESS" : "STANDARD",
            estimatedDays,
            price: Number.isFinite(selectedDeliveryPrice) ? selectedDeliveryPrice : 0,
            description: isSameDay
                ? "Same-day Delivery"
                : isExpress
                    ? "Express Delivery"
                    : "Standard Delivery",
            ...(isSameDay && selectedCourierCompanyId != null
                ? { courierCompanyId: selectedCourierCompanyId }
                : {}),
        };

        const checkoutData: CheckoutDto = {
            cartType: (cart.cartType as CartType) || CartType.ECOMMERCE,
            deliveryAddress,
            deliveryOption,
            customerNotes: deliveryInstructions,
        };

        const safeNum = (v: unknown): number => {
            const n = Number(v);
            return isFinite(n) && n > 0 ? n : 0;
        };

        const walletBalanceMap: Record<string, number> = {};
        if (balance) {
            if (balance.components) {
                balance.components.forEach((comp) => {
                    if (comp.isEligible) {
                        const mappedKey =
                            COMPONENT_TO_SALAR_PAY[comp.component] || comp.component;
                        walletBalanceMap[mappedKey] = safeNum(comp.balance);
                    }
                });
            } else {
                walletBalanceMap.REFERRAL_COMMISSION = safeNum(
                    balance.commissionBalances?.referralCommission
                );
                walletBalanceMap.SALAR_FLOW_COMMISSION = safeNum(
                    balance.commissionBalances?.flowCommission
                );
                walletBalanceMap.SALAR_UNITY_COMMISSION = safeNum(
                    balance.commissionBalances?.unityCommission
                );
                walletBalanceMap.SALAR_COMMUNITY_GROWTH_COMMISSION = safeNum(
                    balance.commissionBalances?.communityGrowthCommission
                );
                walletBalanceMap.SALAR_SAMRIDDHI_COMMISSION = safeNum(
                    balance.commissionBalances?.samriddhiCommission
                );
                walletBalanceMap.SALAR_MILESTONE_REFERRAL_COMMISSION = safeNum(
                    balance.commissionBalances?.milestoneReferralCommission
                );
                walletBalanceMap.SALAR_PEARLS_COMMISSION = safeNum(
                    balance.commissionBalances?.pearlsCommission
                );
                walletBalanceMap.SALAR_FUNDS = safeNum(balance.funds);
                walletBalanceMap.SALAR_PARTNER_GROWTH_REFERRAL = safeNum(
                    balance.commissionBalances?.partnerGrowthReferral
                );
            }
        }

        const grandTotal = safeNum(cart.grandTotal);
        if (grandTotal <= 0) {
            abort("Invalid cart total. Please refresh and try again.");
            return;
        }

        const walletTotal = selectedWallets.reduce(
            (sum, type) => sum + Math.min(walletBalanceMap[type] ?? 0, grandTotal),
            0
        );

        const toAmount = (n: number) => parseFloat(Math.max(n, 0).toFixed(2));

        // 3-condition payment logic
        if (walletTotal <= 0) {
            // Condition 3: online payment only
            checkoutData.onlinePaymentMethod = selectedOnlineMethod as
                | "PHONEPE"
                | "CASHFREE";
        } else if (walletTotal >= grandTotal) {
            // Condition 1: fully covered by Salar Pay
            let remaining = grandTotal;
            checkoutData.salarPaySelection = selectedWallets
                .map((type, idx) => {
                    const available = Math.min(walletBalanceMap[type] ?? 0, remaining);
                    if (available <= 0) return null;
                    const raw =
                        idx === selectedWallets.length - 1 ? remaining : available;
                    const amount = toAmount(raw);
                    remaining = toAmount(remaining - amount);
                    return amount > 0 ? { type: type as SalarPayType, amount } : null;
                })
                .filter((x): x is { type: SalarPayType; amount: number } => x !== null);
        } else {
            // Condition 2: partial wallet + online payment for remaining
            checkoutData.salarPaySelection = selectedWallets
                .map((type) => {
                    const amount = toAmount(walletBalanceMap[type] ?? 0);
                    return amount > 0 ? { type: type as SalarPayType, amount } : null;
                })
                .filter((x): x is { type: SalarPayType; amount: number } => x !== null);
            checkoutData.onlinePaymentMethod = selectedOnlineMethod as
                | "PHONEPE"
                | "CASHFREE";
        }

        try {
            await dispatch(processCheckout(checkoutData)).unwrap();
        } catch (err: unknown) {
            const msg =
                typeof err === "string"
                    ? err
                    : err instanceof Error
                        ? err.message
                        : "Checkout failed";
            toast.error(msg, {
                autoClose: 4000,
                toastId: "checkout-error",
            });
            dispatch(clearCheckoutError());
            console.error("Checkout failed:", err);
        } finally {
            isPlacingOrder.current = false;
            setIsPlacingOrderState(false);
        }
    };

    // Calculate live pricing details from real cart
    const checkoutPricing = useMemo<CheckoutPricing>(() => {
        if (!cart) {
            return {
                totalItems: 0,
                itemsTotal: 0,
                shoppingVoucher: 0,
                salarCoins: 0,
                salarCodeDiscount: 0,
                couponDiscount: 0,
                deliveryFee: 0,
                platformFee: 0,
                orderTotal: 0,
                totalSavings: 0,
            };
        }

        const totalItems = cart.itemsCount ?? 0;
        const itemsTotal = Number(cart.itemsTotal ?? 0);
        const shoppingVoucher = Number(cart.voucherAppliedAmount ?? 0);
        const salarCoins = 0;
        const salarCodeDiscount = Number(cart.salarCodeDiscountAmount ?? 0);
        const couponDiscount = Number(cart.couponDiscountAmount ?? 0);
        const couponCode = cart.couponCode;
        const salarCode = cart.salarCode;
        const isInfluencerCoupon =
            cart.couponType === CouponType.INFLUENCER ||
            (cart.couponType as string) === "INFLUENCER";
        const deliveryFee = Number(cart.deliveryCharges ?? cart.deliveryOptionPrice ?? 0);
        const platformFee = Number(cart.platformFee ?? 0);
        const orderTotal = Number(cart.grandTotal ?? 0);
        const totalSavings = Number(
            cart.totalDiscount ??
                couponDiscount + salarCodeDiscount + shoppingVoucher
        );

        return {
            totalItems,
            itemsTotal,
            shoppingVoucher,
            salarCoins,
            salarCodeDiscount,
            couponDiscount,
            couponCode,
            salarCode,
            totalSavings,
            isInfluencerCoupon,
            deliveryFee,
            platformFee,
            orderTotal,
        };
    }, [cart]);

    return (
        <>
            <CheckoutNavbar />

            <main className="checkout-page">
                <div className="checkout-page-layout">
                    <div className="checkout-page-main">
                        <CheckoutAddressSection
                            addresses={addresses}
                            selectedAddressId={cart?.selectedAddressId}
                            onSelectAddress={handleSelectAddress}
                            userName={user?.fullName || undefined}
                            deliveryInstructions={deliveryInstructions}
                            onDeliveryInstructionsChange={setDeliveryInstructions}
                        />

                        <CheckoutReviewOrder />

                        <div className="checkout-price-details-mobile">
                            <CheckoutPriceDetails pricing={checkoutPricing} />
                        </div>

                        <CheckoutPaymentOptions
                            selectedWallets={selectedWallets}
                            onToggleWallet={handleToggleWallet}
                            selectedOnlineMethod={selectedOnlineMethod}
                            onSelectOnlineMethod={setSelectedOnlineMethod}
                            grandTotal={cart?.grandTotal ?? 0}
                            onPlaceOrder={handlePlaceOrder}
                            isPlacingOrder={isPlacingOrderState}
                            checkoutLoading={checkoutLoading}
                            payDisabled={payDisabled}
                            undeliverable={undeliverable}
                            pdpDeliveryLoading={pdpDeliveryLoading}
                            hasPhysicalItem={hasPhysicalItem}
                            paymentSuccessOpen={paymentSuccessOpen}
                            onSuccessContinue={() => {
                                setPaymentSuccessOpen(false);
                                navigate("/HomePage");
                            }}
                        />
                    </div>

                    <div className="checkout-price-details-desktop">
                        <CheckoutPriceDetails pricing={checkoutPricing} />
                    </div>
                </div>
            </main>
        </>
    );
};

export default CheckoutPage;
